// routes
import { useEffect } from 'react';
import { PATH_ADMIN, PATH_USER, PATH_RESTAURANT_ADMIN, PATH_KITCHEN_DISPLAY_SYSTEM, PATH_CHEF_PRINTER_SETTINGS, PATH_CHEF_POS, PATH_CHEF_ORDERS } from '../../../routes/paths';
// components
import Label from '../../../components/Label';
import Iconify from '../../../components/Iconify';
import SvgIconStyle from '../../../components/SvgIconStyle';
import useAuth from '../../../hooks/useAuth';
import { isHubApp } from '../../../utils/hubApp';
import DeskOSInspectIcon from '../../../assets/DeskOSInspectIcon.svg';

// ----------------------------------------------------------------------

const getIcon = (name) => <SvgIconStyle src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />;
const getIconfy = (name) => <Iconify icon={name} width={22} height={22} />;

const ICONS = {
  blog: getIcon('ic_blog'),
  cart: getIcon('ic_cart'),
  chat: getIcon('ic_chat'),
  mail: getIcon('ic_mail'),
  user: getIcon('ic_user'),
  kanban: getIcon('ic_kanban'),
  banking: getIcon('ic_banking'),
  booking: getIcon('ic_booking'),
  invoice: getIcon('ic_invoice'),
  calendar: getIcon('ic_calendar'),
  ecommerce: getIcon('ic_ecommerce'),
  analytics: getIcon('ic_analytics'),
  dashboard: getIcon('ic_dashboard'),
  menuItem: getIcon('ic_menu_item'),

  // ---------------------------------------
  // Student Icons
  jobs: getIconfy('eva:briefcase-fill'),
  opportunities: getIconfy('maki:college'),
  placements: getIconfy('fluent:people-audience-24-filled'),
  internships: getIconfy('fluent:people-team-24-filled'),
  resume: getIconfy('mingcute:document-line'),
  StudentSettings: getIconfy('eva:settings-fill'),
  studentCareerTrack: getIconfy('eva:arrowhead-up-outline'),
  ctroadmap: getIconfy('streamline:arrow-roadmap'),
  test: getIconfy('material-symbols:quiz'),
  // Student CH
  careerHubHome: getIconfy('eva:home-outline'),
  personalityTest: getIconfy('icon-park-outline:thinking-problem'),
  mockInterview: getIconfy('material-symbols:frame-person-mic'),

  // ---------------------------------------
  // Univeristy Icons
  addStudent: getIconfy('eva:person-add-fill'),
  groups: getIconfy('eva:people-fill'),
  studentActivity: getIconfy('eva:activity-fill'),
  uniJobs: getIconfy('eva:briefcase-fill'),
  studentDetails: getIconfy('eva:person-fill'),
  univeristyEvent: getIconfy('material-symbols:event'),
  uniWorkshops: getIconfy('material-symbols:computer-outline'),
  upgrade: getIconfy('eva:arrowhead-up-outline'),
  add: getIconfy('ic:baseline-add'),

  // ---------------------------------------
  // Company Icons
  newJobs: getIconfy('eva:archive-fill'),
  newJobPosting: getIconfy('ion:create-outline'),
  Applicants: getIconfy('et:profile-male'),

  // TPO
  uniListing: getIconfy('la:university'),
  recListing: getIconfy('hugeicons:permanent-job'),
  howit: getIconfy('material-symbols:help'),
  myContacts: getIconfy('material-symbols:contacts'),
  community: getIconfy('wpf:chat'),
  events: getIconfy('mdi:trophy-outline'),
  myUniversity: getIconfy('hugeicons:university'),

  downloads: getIconfy('eva:download-outline'),
};

const REACT_APP_ENVIRONMENT = process.env.REACT_APP_ENVIRON || '';

// Admin-specific icons using Material Design Icons (mdi) for consistency
const ADMIN_ICONS = {
  // Main Operations
  dashboard: getIconfy('mdi:view-dashboard'),
  checkIns: getIconfy('iconamoon:enter-light'),
  liveStatus: getIconfy('mdi:monitor-dashboard'),
  liveControlCenter: getIconfy('mdi:view-dashboard-variant'),

  // Membership Related
  members: getIconfy('mdi:account-group'),
  subscriptions: getIconfy('mdi:card-account-details'),
  visitPlans: getIconfy('mdi:ticket-account'),

  // Space Management
  spaces: getIconfy('mdi:office-building'),
  meetingRooms: getIconfy('guidance:meeting-room'),
  spaceManagement: getIconfy('mdi:floor-plan'),

  // Lease Mnaagement
  leaseManagement: getIconfy('icon-park-outline:doc-detail'),
  leaseManagementHome: getIconfy('material-symbols:home-outline-rounded'),
  leases: getIconfy('icon-park-outline:doc-detail'),

  // Service Requests
  serviceRequests: getIconfy('mdi:ticket-account'),
  serviceRequestsHome: getIconfy('mingcute:service-fill'),
  workOrders: getIconfy('mdi:clipboard-check-outline'),

  // Expenses & Consumables
  expenses: getIconfy('mdi:receipt-text-outline'),
  expensesAll: getIconfy('mdi:format-list-bulleted'),
  expensesApprovals: getIconfy('mdi:check-decagram-outline'),
  expensesVendors: getIconfy('mdi:store-outline'),
  expensesReports: getIconfy('mdi:chart-bar'),
  expensesSettings: getIconfy('mdi:cog-outline'),
  consumables: getIconfy('material-symbols:inventory'),
  inventoryHome: getIconfy('mdi:home'),
  inventoryPurchase: getIconfy('mdi:cart-plus'),
  inventoryConsumption: getIconfy('mdi:cart-minus'),
  inventoryCurrent: getIconfy('mdi:cart-arrow-down'),
  // Inventory & Orders (kept for legacy)
  inventory: getIconfy('material-symbols:inventory'),
  orders: getIconfy('mdi:cart'),

  ordersList: getIconfy('lets-icons:order'),
  commerce: getIconfy('qlementine-icons:money-16'),

  invoice: getIconfy('iconamoon:invoice-bold'),
  creditNote: getIconfy('lsicon:sales-return-filled'),
  receipts: getIconfy('fluent:receipt-16-filled'),

  // Organization
  organization: getIconfy('mdi:domain'),
  orgMembers: getIconfy('mdi:account-tie'),

  // Settlements
  settlements: getIconfy('mdi:cash-multiple'),

  // Settings
  settings: getIconfy('mdi:cog'),

  // Booking & Status
  bookings: getIconfy('mdi:calendar-clock'),
  status: getIconfy('mdi:desktop-mac-dashboard'),

  // Community
  community: getIconfy('mdi:chat'),
  events: getIconfy('mdi:trophy-outline'),

  leadsManagement: getIconfy('carbon:sales-ops'),
  outreach: getIconfy('mdi:email-multiple-outline'),

  downloads: getIconfy('eva:download-outline'),

  visitorManagement: getIconfy('fluent:guest-12-regular'),
  courier: getIconfy('mdi:package-variant-closed'),
  signage: getIconfy('mdi:television-play'),

  settingsIntegrations: getIconfy('carbon:ibm-cloud-pak-integration'),
  settingsGeneral: getIconfy('mdi:cog-outline'),
  settingsWhatsApp: getIconfy('ic:baseline-whatsapp'),

  paymentManagement: getIconfy('mdi:rupee'),
  settingsImport: getIconfy('mdi:import'),

  // Communication
  communication: getIconfy('material-symbols:mail-outline'),

  // Access Control
  accessControl: getIconfy('mdi:door-closed-lock'),
  network: getIconfy('mdi:wifi'),
  accessEvents: getIconfy('mdi:access-point'),

  // Meeting Room Display
  meetingRoomDisplays: getIconfy('mdi:tablet-dashboard'),

  // Meeting Room Credits
  meetingRoomCredits: getIconfy('mdi:credit-card-plus-outline'),

  dex: getIconfy('mdi:robot-happy'),
  reports: getIconfy('mdi:chart-bar-stacked'),
  automations: getIconfy('mdi:cog-transfer-outline'),
  chatbots: getIconfy('mdi:robot-excited-outline'),

  printing: getIconfy('mdi:printer-pos'),

  sync: getIconfy('material-symbols:sync-rounded'),

  coupons: getIconfy('mdi:coupon-outline'),

  // Financials
  financials: getIconfy('mdi:money'),

  // Website Builder
};

const USER_ICONS = {
  home: getIconfy('material-symbols:home'),
  billing: getIconfy('stash:billing-info'),
  attendance: getIconfy('tdesign:time'),
  settings: getIconfy('eva:settings-2-fill'),
  companySettings: getIconfy('fluent:launcher-settings-24-filled'),
  printing: getIconfy('mdi:printer-pos'),
  courier: getIconfy('mdi:package-variant-closed'),
  refreshments: getIconfy('mdi:coffee-outline'),
};

// ----------------------------------------------------------------------
// Admin nav is grouped by job-to-be-done (not by internal module boundary),
// ordered by daily frequency. Item-level gating (roles / allowed / slug /
// permission) is unchanged — only the section grouping and order differ.
// ----------------------------------------------------------------------
const adminNavConfigAll = [
  {
    subheader: 'Home',
    items: [
      {
        roles: ['org'],
        title: 'Dashboard',
        path: PATH_ADMIN.dashboard,
        icon: ADMIN_ICONS.dashboard,
        allowed: { org: ['all'] },
        permission: 'dashboard.view',
        tourId: 'tour-nav-dashboard',
      },
      {
        roles: ['org'],
        title: 'Dex AI',
        path: PATH_ADMIN.dex,
        icon: ADMIN_ICONS.dex,
        allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
        slug: 'dex',
        permission: 'dashboard.view',
      },
      {
        roles: ['org'],
        title: 'Live Status',
        path: PATH_ADMIN.liveControlCenter,
        icon: ADMIN_ICONS.status,
        allowed: { org: ['admin', 'manager', 'center_manager'] },
        permission: 'dashboard.view',
      },
      // {
      //   roles: ['org'],
      //   title: 'Control Center',
      //   path: PATH_ADMIN.liveControlCenter,
      //   icon: ADMIN_ICONS.liveControlCenter,
      //   allowed: { org: ['admin', 'manager', 'center_manager'] },
      // },
      {
        roles: ['org'],
        title: 'Front Desk',
        path: PATH_ADMIN.checkIns,
        icon: getIconfy('mdi:desk'),
        // Parent group: no permission/slug so it never over-restricts —
        // each child below self-gates exactly as before. allowed = union of children.
        allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
        tourId: 'tour-nav-frontdesk',
        children: [
          // Accountant Management
          {
            roles: ['org'],
            title: 'Check Ins',
            path: PATH_ADMIN.checkIns,
            icon: ADMIN_ICONS.checkIns,
            allowed: { org: ['admin', 'manager'] },
            slug: 'checkin-management',
            permission: 'checkins.view',
          },
          {
            roles: ['org'],
            // Called Rooms because it is not only meeting rooms any more: pods,
            // courts and studios live on the same screen. The path, the slug
            // and the permission are untouched, so nothing an operator has
            // bookmarked or been granted changes.
            title: 'Rooms',
            path: PATH_ADMIN.meetingRoomManagement.root,
            icon: ADMIN_ICONS.meetingRooms,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            slug: 'meeting-room',
            permission: 'meeting_rooms.view',
          },
          {
            roles: ['org'],
            title: 'Service Requests',
            path: PATH_ADMIN.serviceRequests.home,
            icon: ADMIN_ICONS.serviceRequestsHome,
            allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
            slug: 'helpdesk',
            permission: 'service_requests.view',
            children: [
              {
                title: 'Home',
                path: PATH_ADMIN.serviceRequests.home,
                icon: ADMIN_ICONS.serviceRequestsHome,
                allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
                slug: 'helpdesk',
              },
              {
                title: 'All Tickets',
                path: PATH_ADMIN.serviceRequests.tickets,
                icon: ADMIN_ICONS.serviceRequests,
                allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
                slug: 'helpdesk',
              },
              {
                title: 'Work Orders',
                path: PATH_ADMIN.serviceRequests.workOrders,
                icon: ADMIN_ICONS.workOrders,
                allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
                slug: 'helpdesk',
                permission: 'work_orders.view',
              },
            ],
          },
          {
            roles: ['org'],
            title: 'Visitor Management',
            path: PATH_ADMIN.visitorManagement,
            icon: ADMIN_ICONS.visitorManagement,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'visitors.view',
          },
          {
            roles: ['org'],
            title: 'Courier',
            path: PATH_ADMIN.courier,
            icon: ADMIN_ICONS.courier,
            // Staff too: the guard on the desk is the person who logs parcels,
            // and is the only one who ever will.
            allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
            permission: 'courier.view',
          },
          {
            roles: ['org'],
            title: 'Community',
            path: PATH_ADMIN.community,
            icon: ADMIN_ICONS.community,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            slug: 'community',
            permission: 'community.view',
          },
          {
            roles: ['org'],
            title: 'Events & Challenges',
            path: PATH_ADMIN.events.root,
            icon: ADMIN_ICONS.events,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            slug: 'events',
            permission: 'events.view',
          },
        ],
      },
    ],
  },
  {
    subheader: 'Sales & Growth',
    items: [
      {
        roles: ['org'],
        title: 'Leads Management',
        path: PATH_ADMIN.leadsManagement.dashboard,
        icon: ADMIN_ICONS.leadsManagement,
        allowed: { org: ['admin', 'manager'] },
        slug: 'leads-management',
        permission: 'leads.view',
        tourId: 'tour-nav-leads',
        children: [
          {
            title: 'Dashboard',
            path: PATH_ADMIN.leadsManagement.dashboard,
            icon: getIconfy('eva:grid-fill'),
          },
          {
            title: 'My Day',
            path: PATH_ADMIN.leadsManagement.myDay,
            icon: getIconfy('eva:sun-fill'),
          },
          {
            title: 'Leads',
            path: PATH_ADMIN.leadsManagement.list,
            icon: getIconfy('ic:round-people-alt'),
          },
          {
            title: 'Follow-ups',
            path: PATH_ADMIN.leadsManagement.followUps,
            icon: getIconfy('eva:clock-fill'),
          },
          // Invitations not supported yet
          // {
          //   title: 'Invitations',
          //   path: PATH_ADMIN.leadsManagement.invitations,
          //   icon: getIconfy('eva:email-fill'),
          // },
          {
            title: 'Proposals',
            path: PATH_ADMIN.leadsManagement.proposals,
            icon: getIconfy('eva:file-text-fill'),
          },
          {
            title: 'Website',
            path: PATH_ADMIN.leadsManagement.website,
            icon: getIconfy('eva:edit-2-fill'),
          },
          {
            title: 'Reports',
            path: PATH_ADMIN.leadsManagement.reports,
            icon: getIconfy('eva:pie-chart-2-fill'),
            permission: 'team.view',
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Chatbots',
        path: PATH_ADMIN.chatbots.dashboard,
        icon: ADMIN_ICONS.chatbots,
        slug: 'leads-chatbot',
        allowed: { org: ['admin', 'manager'] },
        permission: 'leads.view',
        children: [
          {
            title: 'Dashboard',
            path: PATH_ADMIN.chatbots.dashboard,
            icon: getIconfy('eva:pie-chart-2-fill'),
          },
          {
            title: 'Conversations',
            path: PATH_ADMIN.chatbots.inbox,
            icon: getIconfy('mdi:chat-processing-outline'),
          },
          {
            title: 'AI Knowledge',
            path: PATH_ADMIN.chatbots.knowledge,
            icon: getIconfy('mdi:book-open-page-variant-outline'),
          },
          {
            title: 'Bots & Settings',
            path: PATH_ADMIN.chatbots.settings,
            icon: getIconfy('mdi:robot-excited-outline'),
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Outreach',
        path: PATH_ADMIN.outreach.dashboard,
        icon: ADMIN_ICONS.outreach,
        allowed: { org: ['admin', 'manager'] },
        slug: 'outreach',
        permission: 'settings.manage',
        children: [
          {
            title: 'Dashboard',
            path: PATH_ADMIN.outreach.dashboard,
            icon: getIconfy('eva:pie-chart-2-fill'),
          },
          {
            title: 'Campaigns',
            path: PATH_ADMIN.outreach.campaigns,
            icon: getIconfy('mdi:email-fast-outline'),
          },
          {
            title: 'Contacts',
            path: PATH_ADMIN.outreach.contacts,
            icon: getIconfy('mdi:account-group-outline'),
          },
          {
            title: 'Templates',
            path: PATH_ADMIN.outreach.templates,
            icon: getIconfy('mdi:file-document-outline'),
          },
        ],
      },
    ],
  },
  {
    subheader: 'Members',
    items: [
      {
        roles: ['org'],
        title: 'Memberships',
        path: PATH_ADMIN.members.membershipsPage,
        icon: ADMIN_ICONS.members,
        allowed: { org: ['admin', 'manager', 'center_manager'] },
        slug: 'plans',
        permission: 'members.view',
        tourId: 'tour-nav-members',
      },
      {
        roles: ['org'],
        title: 'Lease Management',
        path: PATH_ADMIN.leaseManagement.home,
        icon: ADMIN_ICONS.leaseManagement,
        allowed: { org: ['admin', 'manager'] },
        slug: 'lease-management',
        // Leases have their own codes now. The module used to sit behind
        // billing.manage, which meant a seller could not draft a contract
        // without also being able to rewrite invoices.
        permission: 'leases.view',
        children: [
          {
            title: 'Home',
            path: PATH_ADMIN.leaseManagement.home,
            icon: ADMIN_ICONS.leaseManagementHome,
            allowed: { org: ['admin', 'manager'] },
            permission: 'leases.view',
          },
          {
            title: 'Leases',
            path: PATH_ADMIN.leaseManagement.leases,
            icon: ADMIN_ICONS.leases,
            allowed: { org: ['admin', 'manager'] },
            permission: 'leases.view',
          },
          {
            title: 'Deposits',
            path: PATH_ADMIN.leaseManagement.deposits,
            icon: getIconfy('mdi:cash-multiple'),
            allowed: { org: ['admin', 'manager'] },
            // Deposits are money, so this one stays on the money code even
            // though the page lives inside Lease Management.
            permission: 'financials.view',
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Virtual Office',
        path: PATH_ADMIN.virtualOffice.list,
        icon: getIconfy('mdi:office-building-outline'),
        allowed: { org: ['admin', 'manager'] },
        slug: 'virtual-office',
        permission: 'spaces.manage',
        children: [
          {
            title: 'Subscriptions',
            path: PATH_ADMIN.virtualOffice.list,
            icon: getIconfy('mdi:office-building-outline'),
            allowed: { org: ['admin', 'manager'] },
          },
          {
            title: 'Amenity Packages',
            path: PATH_ADMIN.virtualOffice.amenityPackages,
            icon: getIconfy('mdi:package-variant-closed'),
            allowed: { org: ['admin', 'manager'] },
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Commerce',
        path: PATH_ADMIN.orderManagement.root,
        icon: ADMIN_ICONS.commerce,
        allowed: { org: ['admin', 'manager', 'center_manager'] },
        // Sales holds subscriptions.manage and not billing.view. The only child
        // they can see is Billing Handoffs, and isItemVisible hides the group
        // when nothing under it is visible, so this does not open the rest.
        permissions: ['billing.view', 'subscriptions.manage'],
        tourId: 'tour-nav-commerce',
        children: [
          {
            title: 'Orders',
            path: PATH_ADMIN.orderManagement.list,
            icon: ADMIN_ICONS.ordersList,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            slug: 'orders',
            permission: 'billing.view',
          },
          {
            title: 'Invoices',
            path: PATH_ADMIN.orderManagement.invoiceList,
            icon: ADMIN_ICONS.invoice,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            slug: 'orders',
            permission: 'billing.view',
          },
          {
            title: 'Receipts',
            path: PATH_ADMIN.orderManagement.receiptList,
            icon: ADMIN_ICONS.receipts,
            allowed: { org: ['admin', 'manager'] },
            slug: 'orders',
            permission: 'financials.view',
          },
          {
            title: 'Payment Management',
            path: PATH_ADMIN.orderManagement.paymentManagement,
            icon: ADMIN_ICONS.paymentManagement,
            allowed: { org: ['admin', 'manager'] }, // center_manager NOT included
            slug: 'orders',
            permission: 'financials.view',
          },
          {
            title: 'Collection Register',
            path: PATH_ADMIN.orderManagement.collectionRegister,
            icon: getIconfy('mdi:bank-check'),
            allowed: { org: ['admin', 'manager', 'accountant'] },
            slug: 'orders',
            permission: 'financials.view',
          },
          {
            // Sales sees this too: it is where their sent deals live until
            // accounts invoices them.
            title: 'Billing Handoffs',
            path: PATH_ADMIN.orderManagement.billingHandoffs,
            icon: getIconfy('mdi:inbox-arrow-down-outline'),
            allowed: { org: ['admin', 'manager', 'center_manager', 'accountant'] },
            slug: 'orders',
            permissions: ['billing.view', 'subscriptions.manage'],
            badgeSource: 'billingHandoffs',
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Reports',
        path: PATH_ADMIN.reports.root,
        icon: ADMIN_ICONS.reports,
        allowed: { org: ['admin'] },
        slug: 'reports',
        permission: 'reports.view',
        tourId: 'tour-nav-reports',
      },
      {
        roles: ['org'],
        title: 'Analytics',
        path: PATH_ADMIN.analytics.root,
        icon: getIconfy('mdi:chart-box-outline'),
        permission: 'analytics.view',
      },
    ],
  },
  {
    subheader: 'Facilities',
    items: [
      {
        roles: ['org'],
        title: 'Parking',
        path: PATH_ADMIN.parkingManagement.root,
        icon: getIconfy('mdi:parking'),
        allowed: { org: ['admin', 'manager', 'center_manager'] },
        slug: 'parking',
        permission: 'spaces.view',
      },
      {
        roles: ['org'],
        title: 'Asset Management',
        path: PATH_ADMIN.assetManagement.root,
        icon: getIconfy('mdi:package-variant-closed'),
        allowed: { org: ['admin', 'manager', 'center_manager'] },
        slug: 'asset-management',
        permission: 'assets.view',
        tourId: 'tour-nav-assets',
        children: [
          {
            roles: ['org'],
            title: 'All Assets',
            path: PATH_ADMIN.assetManagement.root,
            icon: getIconfy('mdi:view-list'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Scan',
            path: PATH_ADMIN.assetManagement.scan,
            icon: getIconfy('mdi:qrcode-scan'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Floor Map',
            path: PATH_ADMIN.assetManagement.floorMap,
            icon: getIconfy('mdi:map-marker-multiple'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Audits',
            path: PATH_ADMIN.assetManagement.audits,
            icon: getIconfy('mdi:clipboard-check-outline'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Analytics',
            path: PATH_ADMIN.assetManagement.analytics,
            icon: getIconfy('mdi:chart-bar'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Maintenance',
            path: PATH_ADMIN.assetManagement.maintenance,
            icon: getIconfy('mdi:wrench-clock'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Damage Reports',
            path: PATH_ADMIN.assetManagement.damage,
            icon: getIconfy('mdi:alert-circle-outline'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'AMC',
            path: PATH_ADMIN.assetManagement.amc,
            icon: getIconfy('mdi:file-document-check'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Insurance',
            path: PATH_ADMIN.assetManagement.insurance,
            icon: getIconfy('mdi:shield-check-outline'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            // Qualified, because Expenses has a Vendors list too and these are a
            // different thing: the people who service and maintain assets.
            title: 'Service Vendors',
            path: PATH_ADMIN.assetManagement.vendors,
            icon: getIconfy('mdi:handshake'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Assignments',
            path: PATH_ADMIN.assetManagement.assignments,
            icon: getIconfy('mdi:account-key'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            roles: ['org'],
            title: 'Settings',
            path: PATH_ADMIN.assetManagement.settings,
            icon: getIconfy('mdi:cog'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'assets.manage',
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Manage',
        path: PATH_ADMIN.SpaceManagement,
        icon: ADMIN_ICONS.organization,
        allowed: { org: ['admin', 'manager'] },
        permission: 'spaces.manage',
        tourId: 'tour-nav-spaces',
        children: [
          {
            roles: ['org'],
            title: 'Space Management',
            path: PATH_ADMIN.SpaceManagement,
            icon: ADMIN_ICONS.spaceManagement,
            allowed: { org: ['admin', 'manager'] },
            permission: 'spaces.manage',
          },
          {
            roles: ['org'],
            title: 'Plans',
            path: PATH_ADMIN.plans.root,
            icon: ADMIN_ICONS.visitPlans,
            allowed: { org: ['admin', 'manager'] },
            permission: 'plans.view',
          },
          {
            roles: ['org'],
            title: 'Organization Members',
            path: PATH_ADMIN.orgMember.root,
            icon: ADMIN_ICONS.orgMembers,
            allowed: { org: ['admin', 'manager'] },
            permission: 'team.view',
          },
          {
            roles: ['org'],
            title: 'Coupons',
            path: PATH_ADMIN.coupons,
            icon: ADMIN_ICONS.coupons,
            allowed: { org: ['admin', 'manager'] },
            permission: 'billing.view',
          }
          // {
          //   roles: ['org'],
          //   title: 'Settlements',
          //   path: PATH_ADMIN.settlements.root,
          //   icon: ADMIN_ICONS.settlements
          // }
        ],
      },
      {
        roles: ['org'],
        title: 'Expenses',
        path: PATH_ADMIN.expenses.root,
        icon: ADMIN_ICONS.expenses,
        allowed: { org: ['admin', 'manager', 'center_manager'] },
        slug: 'expenses',
        permission: 'expenses.view',
        tourId: 'tour-nav-expenses',
        children: [
          // Ordered the way the work runs: what needs me now, then the things
          // that create spend, then the things that measure it.
          {
            title: 'Control Center',
            path: PATH_ADMIN.expenses.dashboard,
            icon: ADMIN_ICONS.expenses,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'financials.view',
          },
          {
            title: 'All Expenses',
            path: PATH_ADMIN.expenses.all,
            icon: ADMIN_ICONS.expensesAll,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            title: 'Recurring',
            path: PATH_ADMIN.expenses.recurring,
            icon: getIconfy('mdi:autorenew'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            title: 'Vendors',
            path: PATH_ADMIN.expenses.vendors,
            icon: ADMIN_ICONS.expensesVendors,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
          },
          {
            // The store room is the one part of Expenses that still runs on
            // inventory.*, so a role can be given spend without stock.
            title: 'Consumables',
            path: PATH_ADMIN.expenses.consumables,
            icon: ADMIN_ICONS.consumables,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'inventory.view',
            children: [
              {
                title: 'Current Inventory',
                path: PATH_ADMIN.expenses.consumablesInventory,
                icon: ADMIN_ICONS.inventoryCurrent,
                allowed: { org: ['admin', 'manager', 'center_manager'] },
                permission: 'inventory.view',
              },
              {
                title: 'Purchases',
                path: PATH_ADMIN.expenses.consumablesPurchases,
                icon: ADMIN_ICONS.inventoryPurchase,
                allowed: { org: ['admin', 'manager', 'center_manager'] },
                permission: 'inventory.view',
              },
              {
                title: 'Consumption Log',
                path: PATH_ADMIN.expenses.consumablesConsumption,
                icon: ADMIN_ICONS.inventoryConsumption,
                allowed: { org: ['admin', 'manager', 'center_manager'] },
                permission: 'inventory.view',
              },
            ],
          },
          {
            title: 'Petty Cash',
            path: PATH_ADMIN.expenses.pettyCash,
            icon: getIconfy('mdi:cash-multiple'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'financials.view',
          },
          {
            title: 'Budgets',
            path: PATH_ADMIN.expenses.budgets,
            icon: getIconfy('mdi:wallet-outline'),
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'financials.view',
          },
          {
            title: 'Reports',
            path: PATH_ADMIN.expenses.reports,
            icon: ADMIN_ICONS.expensesReports,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'financials.view',
          },
          {
            title: 'Settings',
            path: PATH_ADMIN.expenses.settings,
            icon: ADMIN_ICONS.expensesSettings,
            allowed: { org: ['admin', 'manager'] },
            permission: 'expenses.manage',
          },
        ],
      },
    ],
  },
  {
    subheader: 'Apps',
    items: [
      {
        roles: ['org'],
        title: 'Cafe',
        path: PATH_ADMIN.mealosAdmin.root,
        icon: getIconfy('mdi:food'),
        slug: 'cafe',
        permission: 'cafe.view',
        children: [
          // {
          //   title: 'Dashboard',
          //   path: PATH_ADMIN.mealosAdmin.root,
          //   icon: getIconfy('mdi:view-dashboard'),
          //   slug: 'cafe',
          // },
          {
            title: 'Cafeteria Management',
            path: PATH_ADMIN.mealosAdmin.cafeteriaManagement,
            icon: getIconfy('mdi:food-fork-drink'),
            slug: 'cafe',
            permission: 'cafe.manage',
          },
          {
            title: 'Staff Assignment',
            path: PATH_ADMIN.mealosAdmin.staffAssignment,
            icon: getIconfy('mdi:account-tie'),
            slug: 'cafe',
            permission: 'cafe.manage',
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Shop',
        path: PATH_ADMIN.shopAdmin.root,
        icon: getIconfy('mdi:store'),
        allowed: { org: ['admin', 'manager'] },
        slug: 'shop',
        permission: 'cafe.manage',
        children: [
          {
            title: 'Shop Management',
            path: PATH_ADMIN.shopAdmin.root,
            icon: getIconfy('mdi:store-outline'),
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Printing',
        path: PATH_ADMIN.printing.root,
        icon: ADMIN_ICONS.printing,
        allowed: { org: ['admin', 'manager'] },
        slug: 'printing',
        permission: 'printing.view',
      },
      {
        roles: ['org'],
        title: 'Signage',
        path: PATH_ADMIN.signage,
        icon: ADMIN_ICONS.signage,
        // Staff too: the desk holds signage.view so it can tell that the lobby
        // screen has gone dark. Changing what is on it needs signage.manage,
        // which the page checks itself.
        allowed: { org: ['admin', 'manager', 'center_manager', 'staff'] },
        slug: 'signage',
        permission: 'signage.view',
      },
      {
        roles: ['org'],
        title: 'Inspect',
        path: PATH_ADMIN.inspectAdmin.root,
        icon: <SvgIconStyle src={DeskOSInspectIcon} sx={{ width: 1, height: 1 }} />,
        allowed: { org: ['admin', 'manager'] },
        slug: 'inspect',
        permission: 'inspect.view',
        tourId: 'tour-nav-inspect',
        children: [
          {
            title: 'Overview',
            path: PATH_ADMIN.inspectAdmin.overview,
            icon: getIconfy('mdi:view-dashboard-outline'),
          },
          {
            title: 'Issues',
            path: PATH_ADMIN.inspectAdmin.issues,
            icon: getIconfy('mdi:alert-circle-outline'),
          },
          // Records, not Sessions. Nobody opens this to read a list of rounds.
          // They open it because somebody is disputing a month.
          {
            title: 'Records',
            path: PATH_ADMIN.inspectAdmin.records,
            icon: getIconfy('mdi:file-search-outline'),
          },
          {
            title: 'Setup',
            path: PATH_ADMIN.inspectAdmin.setup,
            icon: getIconfy('mdi:cog-outline'),
            permission: 'inspect.manage',
          },
          {
            title: 'Electricity',
            path: PATH_ADMIN.inspectAdmin.electricity,
            icon: getIconfy('mdi:lightning-bolt'),
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Access Control',
        path: PATH_ADMIN.accessControl.root,
        icon: ADMIN_ICONS.accessControl,
        allowed: { org: ['admin', 'manager'] },
        slug: 'access-control',
        permission: 'access_control.view',
        children: [
          {
            title: 'Users',
            path: PATH_ADMIN.accessControl.users,
            icon: ADMIN_ICONS.members,
            allowed: { org: ['admin', 'manager'] },
          },
          {
            // Hidden for providers with no card support (Kisi rejects physical
            // cards outright); shown while the provider is still unresolved.
            title: 'Card Assignment',
            path: PATH_ADMIN.accessControl.cards,
            icon: getIconfy('mdi:card-account-details'),
            allowed: { org: ['admin', 'manager'] },
            capability: 'cards',
          },
          // The provider setup pages ("Setup" for DeskOS Access, "Kisi Setup")
          // used to sit here, where every org saw both regardless of what it
          // runs. They now live in the Door Management header, next to the
          // doors they configure — see pages/admin/spaceManagement/door.
          {
            roles: ['org'],
            title: 'Door Management',
            path: PATH_ADMIN.doorManagement,
            icon: getIconfy('mdi:door-closed-lock'),
            allowed: { org: ['admin', 'manager'] },
            permission: 'access_control.manage',
          },
          {
            // Both of these read the AccessEvent table, which only exists for a
            // provider that reports events. Code Unlock has no reader at all —
            // a code is typed into a lock that never tells us anything — so the
            // pages would be permanently empty. Kisi is in the same position:
            // it exposes no event feed, webhook or poll (see its adapter).
            // capability, not slug, so a vendor that gains an event feed later
            // gets these back with no change here; an unresolved provider keeps
            // them, because hiding a page an existing customer uses is worse
            // than showing one they cannot fill.
            title: 'Access Events',
            path: PATH_ADMIN.accessControl.eventLogs,
            icon: ADMIN_ICONS.accessEvents,
            allowed: { org: ['admin', 'manager'] },
            capability: 'event_push',
          },
          {
            // Who is inside, what was refused, and whether the doors are
            // reporting. The period reports live in Analytics. Presence,
            // denials and reader health are all derived from access events, so
            // this is gated with the log above.
            title: 'Access Insights',
            path: PATH_ADMIN.accessControl.insights,
            icon: getIconfy('mdi:chart-box-outline'),
            allowed: { org: ['admin', 'manager'] },
            capability: 'event_push',
          },
        ],
      },
      {
        // Member WiFi. Sits beside Access Control because it is the same
        // question about a different door.
        //
        // userFlag hides it entirely for an org with no captive portal set up.
        // The portal itself is configured under Settings -> Integrations; this
        // page only manages members against whatever is configured there, so
        // with nothing configured there is nothing here to do.
        roles: ['org'],
        title: 'Network Access',
        path: PATH_ADMIN.network.root,
        icon: ADMIN_ICONS.network,
        allowed: { org: ['admin', 'manager'] },
        permission: 'wifi.view',
        userFlag: 'wifi_enabled',
      },
    ],
  },
  {
    subheader: 'Automation & Settings',
    items: [
      {
        roles: ['org'],
        title: 'Automations',
        path: PATH_ADMIN.automations,
        icon: ADMIN_ICONS.automations,
        allowed: { org: ['admin'] },
        permission: 'settings.manage',
      },
      {
        roles: ['org'],
        title: 'Communication',
        path: PATH_ADMIN.communication,
        icon: ADMIN_ICONS.communication,
        allowed: { org: ['admin', 'manager'] },
        permission: 'settings.view',
      },
      {
        // Approvals waiting on this person plus the exceptions the system has
        // raised. No permission: the approvals tab is personal, so anyone who
        // can be routed a request needs to be able to reach it.
        roles: ['org'],
        title: 'Inbox',
        path: PATH_ADMIN.inbox.root,
        icon: getIconfy('mdi:inbox-arrow-down-outline'),
      },
      {
        roles: ['org'],
        title: 'DeskOS Support',
        path: PATH_ADMIN.deskosSupport.root,
        icon: getIconfy('mdi:face-agent'),
        permission: 'deskos_support.view',
      },
      {
        // The operator's OWN team, not the members of the space. This is the
        // whole module, not a settings page: the register, the sheet, leave,
        // payroll and the configuration all live under it, and Settings is the
        // last item rather than the way in. Gated by the `workforce` plan
        // feature; the entry itself carries no permission so that "My
        // attendance" stays reachable by anyone with an employee record, which
        // is the point of showing people their own hours.
        //
        // `flyout` means these eleven items do NOT unfold into the main nav.
        // Eleven rows inside an already long list is how a sidebar stops being
        // navigable. Instead the entry opens a second panel beside the nav,
        // and the main nav shrinks to its icon rail while that panel is up.
        // Workforce is the first module to work this way; the rest follow.
        roles: ['org'],
        title: 'Org Attendance',
        path: PATH_ADMIN.workforce.root,
        icon: getIconfy('mdi:account-clock-outline'),
        slug: 'workforce',
        flyout: true,
        // `group` is read only by the flyout panel. The main nav ignores it, so
        // adding it here changes nothing for any other module.
        children: [
          {
            // The same place the module entry itself goes. Worth a row anyway:
            // once you are three screens deep, "back to the dashboard" should
            // not mean hunting for the parent item in the rail behind you.
            title: 'Overview',
            group: 'Every day',
            path: PATH_ADMIN.workforce.root,
            exact: true,
            icon: getIconfy('mdi:view-dashboard-outline'),
            permission: 'attendance.view',
          },
          {
            title: 'Attendance',
            group: 'Every day',
            path: PATH_ADMIN.workforce.register,
            icon: getIconfy('mdi:calendar-check-outline'),
            permission: 'attendance.view',
          },
          {
            title: 'Monthly Sheet',
            group: 'Every day',
            path: PATH_ADMIN.workforce.muster,
            icon: getIconfy('mdi:table-large'),
            permission: 'attendance.view',
          },
          {
            title: 'Staff',
            group: 'People',
            path: PATH_ADMIN.workforce.staff,
            icon: getIconfy('mdi:account-badge-outline'),
            permission: 'attendance.view',
          },
          {
            title: 'Leave',
            group: 'People',
            path: PATH_ADMIN.workforce.leave,
            icon: getIconfy('mdi:calendar-account-outline'),
            permission: 'leave.view',
          },
          {
            title: 'Requests',
            group: 'Every day',
            path: PATH_ADMIN.workforce.requests,
            icon: getIconfy('mdi:file-document-edit-outline'),
            permission: 'attendance.view',
          },
          {
            title: 'Roster',
            group: 'Every day',
            path: PATH_ADMIN.workforce.roster,
            icon: getIconfy('mdi:calendar-multiselect-outline'),
            permission: 'attendance.view',
          },
          {
            title: 'Payroll',
            group: 'Pay',
            path: PATH_ADMIN.workforce.payroll,
            icon: getIconfy('mdi:cash-multiple'),
            permission: 'payroll.view',
          },
          {
            title: 'Advances & Claims',
            group: 'Pay',
            path: PATH_ADMIN.workforce.advances,
            icon: getIconfy('mdi:hand-coin-outline'),
            permission: 'payroll.view',
          },
          {
            // Ten reports behind one entry. It carries the loosest of their
            // permissions so a payroll manager who cannot see attendance still
            // finds the wage bill trend; the page itself only lists what the
            // person may actually run.
            title: 'Reports',
            group: 'Insight',
            path: PATH_ADMIN.workforce.reports,
            icon: getIconfy('mdi:chart-box-outline'),
            permissions: ['attendance.view', 'leave.view', 'payroll.view', 'payroll.view_salary'],
          },
          {
            title: 'My Attendance',
            group: 'Yours',
            path: PATH_ADMIN.workforce.me,
            icon: getIconfy('mdi:account-clock'),
          },
          {
            // Ungated: the list is built from who reports to you, so a person
            // with no reportees sees an empty page rather than a 403, and a
            // manager never has to be granted a code to see their own team.
            title: 'My Team',
            group: 'Yours',
            path: PATH_ADMIN.workforce.myTeam,
            icon: getIconfy('mdi:account-group-outline'),
          },
          {
            // Your own payslips. Ungated for the same reason My Attendance is.
            title: 'My Pay',
            group: 'Yours',
            path: PATH_ADMIN.workforce.myPay,
            icon: getIconfy('mdi:receipt-text-outline'),
          },
          {
            // Holidays, shifts, week offs, leave types and the payroll rules.
            // Reachable from inside every page of the module too, so nobody
            // has to come back out to the nav to fix a holiday.
            title: 'Settings',
            group: 'Setup',
            path: PATH_ADMIN.workforce.settings,
            icon: getIconfy('mdi:cog-outline'),
            permission: 'attendance.settings',
          },
        ],
      },
      {
        roles: ['org'],
        title: 'Settings',
        path: PATH_ADMIN.settings.root,
        icon: ADMIN_ICONS.settings,
        allowed: { org: ['admin'] },
        permission: 'settings.view',
        children: [
          {
            title: 'General',
            path: PATH_ADMIN.settings.general,
            icon: ADMIN_ICONS.settingsGeneral,
            allowed: { org: ['admin'] },
            permission: 'settings.manage',
          },
          {
            title: 'Integrations',
            path: PATH_ADMIN.settings.integrations,
            icon: ADMIN_ICONS.settingsIntegrations,
            allowed: { org: ['admin'] },
            permission: 'settings.manage',
          },
          {
            title: 'WhatsApp',
            path: PATH_ADMIN.settings.whatsapp,
            icon: ADMIN_ICONS.settingsWhatsApp,
            allowed: { org: ['admin'] },
            // Only orgs that have bought the Tech Provider add-on. Everyone else keeps
            // Settings, Integrations, WhatsApp for their existing vendor.
            slug: 'whatsapp_cloud',
            permission: 'settings.view',
          },
          // AI Credits deliberately has no nav entry. It lives inside Settings,
          // under AI Assistant, and keeps its financials.view gate there.
          {
            title: 'Import Hub',
            path: PATH_ADMIN.settings.imports,
            icon: ADMIN_ICONS.settingsImport,
            allowed: { org: ['admin'] },
            permission: 'settings.manage',
          },
          {
            roles: ['org'],
            title: 'Room Displays',
            path: PATH_ADMIN.meetingRoomDisplays,
            icon: ADMIN_ICONS.meetingRoomDisplays,
            allowed: { org: ['admin', 'manager', 'center_manager'] },
            permission: 'settings.view',
          },
        ],
      },
    ],
  },
];

// Sections that are not mobile-friendly yet, so the DeskOS Hub WebView does not
// show them. Temporary — drop entries from this list as each section is made to
// work on a phone. The website is unaffected.
const HUB_HIDDEN_SUBHEADERS = ['Members', 'Facilities', 'Apps', 'Automation & Settings'];

export const adminNavConfig = isHubApp()
  ? adminNavConfigAll.filter((group) => !HUB_HIDDEN_SUBHEADERS.includes(group.subheader))
  : adminNavConfigAll;

const userNavConfig = [
  {
    subheader: 'Home',
    items: [
      {
        roles: ['user'],
        title: 'Dashboard',
        path: PATH_USER.dashboard,
        icon: ICONS.dashboard,
      },
      {
        roles: ['user'],
        title: 'Community',
        path: PATH_USER.feed,
        icon: ICONS.community,
        allowed: { user: ['admin', 'member'] },
        guestHidden: true,
        slug: 'community',
        requiresActivePlan: true,
      },
      {
        roles: ['user'],
        title: 'Events',
        path: PATH_USER.events,
        icon: ICONS.events,
        allowed: { user: ['admin', 'member'] },
        slug: 'events',
        requiresActivePlan: true,
      },
      {
        roles: ['user'],
        title: 'Profile',
        path: PATH_USER.profile,
        icon: ICONS.user,
      },
      {
        roles: ['user'],
        // The one storefront (unified plans, Phase 3): seats, passes, credit
        // packs and every operator type. Feature switches filter the cards.
        title: 'Plans',
        path: PATH_USER.planStore,
        icon: ICONS.cart,
        allowed: { user: ['admin', 'member'] },
      },
      {
        roles: ['user'],
        title: 'Orders',
        path: PATH_USER.myOrders,
        icon: ICONS.cart,
        allowed: { user: ['admin'] },
        slug: 'orders',
      },
      {
        roles: ['user'],
        // Rooms, to match the admin side: a member books pods and courts here
        // too. The path and the slug are untouched, so nothing a member has
        // bookmarked and no access rule changes.
        title: 'Rooms',
        path: PATH_USER.meetingRoom,
        icon: ICONS.booking,
        allowed: { user: ['admin', 'member'] },
        slug: 'meeting-room',
      },
      {
        roles: ['user'],
        title: 'My Visits',
        path: PATH_USER.myVisits,
        icon: ICONS.calendar,
        allowed: { user: ['admin', 'member'] },
        guestHidden: true,
        slug: 'visits',
      },
      {
        roles: ['user'],
        title: 'Courier',
        path: PATH_USER.courier,
        icon: USER_ICONS.courier,
        allowed: { user: ['admin', 'member'] },
        slug: 'courier',
      },
      {
        // Ordering to a desk. Hidden unless the member's active campus serves
        // desks, because a booked room already carries its own ordering inside
        // the booking and needs no nav entry. The flag is resolved per campus,
        // so a member who switches to a campus that does not serve desks loses
        // the item on their next login rather than being sent to a page that
        // can only say no.
        roles: ['user'],
        title: 'Refreshments',
        path: PATH_USER.refreshments,
        icon: USER_ICONS.refreshments,
        allowed: { user: ['admin', 'member'] },
        userFlag: 'refreshments_enabled',
      },
      {
        roles: ['user'],
        title: 'Printing',
        path: PATH_USER.printing.root,
        icon: USER_ICONS.printing,
        allowed: { user: ['admin', 'member'] },
        slug: 'printing',
      },
      {
        roles: ['user'],
        title: 'Company Management',
        path: PATH_USER.companyManagement.root,
        icon: ICONS.groups,
        allowed: { user: ['admin'] },
        guestHidden: true,
        children: [
          {
            title: 'Home',
            path: PATH_USER.companyManagement.root,
            icon: USER_ICONS.home,
            allowed: { user: ['admin'] },
            slug: 'advanced-company-management',
          },
          {
            title: 'Attendance',
            path: PATH_USER.companyManagement.attendance,
            icon: USER_ICONS.attendance,
            allowed: { user: ['admin'] },
            slug: 'advanced-company-management',
          },
          {
            title: 'Members',
            path: PATH_USER.companyManagement.teamMembers,
            icon: ICONS.groups,
            allowed: { user: ['admin'] },
          },
          // {
          //   title: 'Billing',
          //   path: PATH_USER.companyManagement.billing,
          //   icon: USER_ICONS.billing,
          //   allowed: { user: ['admin'] },
          // },
          {
            title: 'Company Settings',
            path: PATH_USER.companyManagement.settings,
            icon: USER_ICONS.companySettings,
            allowed: { user: ['admin'] },
          },
        ],
      },
      {
        roles: ['user'],
        title: 'Virtual Office',
        path: PATH_USER.virtualOffice,
        icon: getIconfy('mdi:office-building-outline'),
      },
      {
        title: 'Settings',
        path: PATH_USER.settings,
        icon: USER_ICONS.settings,
      },
      {
        roles: ['user'],
        title: 'Cafe',
        icon: getIconfy('mdi:food'),
        slug: 'cafe',
        children: [
          {
            title: 'Cafeteria List',
            path: PATH_USER.mealos.cafeteriaList,
            icon: getIconfy('mdi:food-fork-drink'),
            slug: 'cafe',
          },
          {
            title: 'Orders',
            path: PATH_USER.mealos.orders,
            icon: getIconfy('mdi:clipboard-list'),
            slug: 'cafe',
          },
        ],
      },
    ],
  },
];

// Add icons for restaurant admin
const RESTAURANT_ADMIN_ICONS = {
  dashboard: getIconfy('mdi:view-dashboard'),
  menu: getIconfy('mdi:food-fork-drink'),
  orders: getIconfy('mdi:receipt'),
  kitchenDisplay: getIconfy('mdi:chef-hat'),
  inventory: getIconfy('mdi:clipboard-list'),
  rawInventory: getIconfy('mdi:warehouse'),
  settings: getIconfy('mdi:cog'),
  settingsGeneral: getIconfy('mdi:cog-outline'),
  settingsIntegrations: getIconfy('carbon:ibm-cloud-pak-integration'),
  staffManagement: getIconfy('mdi:account-tie'),
  pos: getIconfy('mdi:point-of-sale'),
  coupons: getIconfy('mdi:ticket-percent'),
  mealSchedule: getIconfy('mdi:calendar-edit'),
  printer: getIconfy('mdi:printer'),
  import: getIconfy('mdi:file-upload-outline'),
};

const restaurantAdminNavConfig = [
  {
    subheader: 'Kitchen',
    items: [
      {
        title: 'Kitchen Display System',
        path: PATH_RESTAURANT_ADMIN.kitchenDisplay,
        icon: RESTAURANT_ADMIN_ICONS.kitchenDisplay,
      },
    ],
  },
  {
    subheader: 'Operations',
    items: [
      {
        title: 'Dashboard',
        path: PATH_RESTAURANT_ADMIN.dashboard,
        icon: RESTAURANT_ADMIN_ICONS.dashboard,
      },
      {
        title: 'POS',
        path: PATH_RESTAURANT_ADMIN.pos,
        icon: RESTAURANT_ADMIN_ICONS.pos,
      },
      {
        title: 'Orders',
        path: PATH_RESTAURANT_ADMIN.ordersManagement,
        icon: RESTAURANT_ADMIN_ICONS.orders,
      },
      {
        title: 'Menu',
        path: PATH_RESTAURANT_ADMIN.menuManagement,
        icon: RESTAURANT_ADMIN_ICONS.menu,
      },
      {
        title: 'Inventory',
        path: PATH_RESTAURANT_ADMIN.inventoryManagement,
        icon: RESTAURANT_ADMIN_ICONS.inventory,
      },
      {
        title: 'Raw Inventory',
        path: PATH_RESTAURANT_ADMIN.rawInventory,
        icon: RESTAURANT_ADMIN_ICONS.rawInventory,
        children: [
          { title: 'Overview', path: PATH_RESTAURANT_ADMIN.inventory.dashboard, icon: getIconfy('mdi:view-dashboard-outline') },
          { title: 'Stock', path: PATH_RESTAURANT_ADMIN.inventory.stock, icon: getIconfy('mdi:package-variant-closed') },
          { title: 'Purchasing', path: PATH_RESTAURANT_ADMIN.inventory.purchasing, icon: getIconfy('mdi:cart-outline') },
          { title: 'Goods Receipts', path: PATH_RESTAURANT_ADMIN.inventory.grns, icon: getIconfy('mdi:receipt-text-outline') },
          { title: 'Stock Takes', path: PATH_RESTAURANT_ADMIN.inventory.stockTakes, icon: getIconfy('mdi:clipboard-check-outline') },
          { title: 'Reports', path: PATH_RESTAURANT_ADMIN.inventory.reports, icon: getIconfy('mdi:chart-box-outline') },
        ],
      },
      {
        title: 'Meal Schedule',
        path: PATH_RESTAURANT_ADMIN.mealSchedule,
        icon: RESTAURANT_ADMIN_ICONS.mealSchedule,
      },
      {
        title: 'Settings',
        path: PATH_RESTAURANT_ADMIN.cafeSettings,
        icon: RESTAURANT_ADMIN_ICONS.settings,
        children: [
          {
            title: 'Cafeteria',
            path: PATH_RESTAURANT_ADMIN.cafeSettings,
            icon: RESTAURANT_ADMIN_ICONS.settingsGeneral,
          },
          {
            title: 'Coupons',
            path: PATH_RESTAURANT_ADMIN.coupons,
            icon: RESTAURANT_ADMIN_ICONS.coupons,
          },
          {
            title: 'Integrations',
            path: PATH_RESTAURANT_ADMIN.integrations,
            icon: RESTAURANT_ADMIN_ICONS.settingsIntegrations,
          },
          {
            title: 'Import',
            path: PATH_RESTAURANT_ADMIN.importCenter,
            icon: RESTAURANT_ADMIN_ICONS.import,
          },
        ],
      },
    ],
  },
];

const chefNavConfig = [
  {
    subheader: 'Chef',
    items: [
      {
        title: 'Kitchen Display System',
        path: PATH_KITCHEN_DISPLAY_SYSTEM,
        icon: RESTAURANT_ADMIN_ICONS.kitchenDisplay,
      },
      {
        title: 'POS',
        path: PATH_CHEF_POS,
        icon: RESTAURANT_ADMIN_ICONS.pos,
      },
      {
        title: 'Orders',
        path: PATH_CHEF_ORDERS,
        icon: RESTAURANT_ADMIN_ICONS.orders,
      },
      {
        title: 'Printer Settings',
        path: PATH_CHEF_PRINTER_SETTINGS,
        icon: RESTAURANT_ADMIN_ICONS.printer,
      },
    ],
  },
];

// Boss sub-roles that belong to the boss business system (orgs, orders,
// billing, CRM). Everything except `publisher`, which is a deskos.net content
// role. Mirrors CONTENT_ONLY_BOSS_ROLES in accounts/boss/scoping.py, which is
// where it is actually enforced.
const BOSS_STAFF_ROLES = ['super_admin', 'manager', 'relationship_manager'];

// The build system: sprints, stories and deploy requests. Only the founder and
// the two build sub-roles see it, which is why every item under Build names
// these roles explicitly. Mirrors BUILD_ROLES in routes/index.js and
// deskos_billing/sprints/permissions.py.
const BUILD_ROLES = ['super_admin', 'developer', 'product_manager'];

// Training requests are worked by every boss sub-role except developer.
// Mirrors TRAINING_ROLES in routes/index.js and is_training_staff in
// deskos_billing/training/views.py.
const TRAINING_ROLES = ['super_admin', 'manager', 'relationship_manager', 'publisher', 'product_manager'];

// Single shared boss nav for super_admin, manager, relationship_manager and
// publisher. A manager's / relationship manager's view of Orders and
// Organizations is scoped to the companies they created (enforced
// backend-side). Monthly Billing, Email Settings and Support Config remain
// super_admin-only; the Sales CRM is hidden from relationship managers, whose
// workspace is the Support Tickets queue. A publisher sees the Website section
// and nothing else, so every other item names its roles explicitly: an item
// with no `allowed` is visible to every boss sub-role.
const bossNavConfig = [
  {
    subheader: 'DeskOS',
    items: [
      {
        title: 'Orders',
        path: '/boss/order/list',
        icon: getIconfy('mdi:receipt-outline'),
        allowed: { boss: BOSS_STAFF_ROLES },
      },
      {
        title: 'Organizations',
        path: '/boss/manager/organizations',
        icon: getIconfy('mdi:domain'),
        allowed: { boss: BOSS_STAFF_ROLES },
      },
      {
        title: 'Onboard Organization',
        path: '/boss/organization/create',
        icon: getIconfy('mdi:plus-circle-outline'),
        allowed: { boss: BOSS_STAFF_ROLES },
      },
      {
        title: 'Monthly Billing',
        path: '/boss/billing',
        icon: getIconfy('mdi:calendar-month'),
        allowed: { boss: ['super_admin'] },
      },
      {
        title: 'Email Settings',
        path: '/boss/settings/email',
        icon: getIconfy('mdi:email-cog-outline'),
        allowed: { boss: ['super_admin'] },
      },
      {
        // Health of the Meta app every client's WhatsApp runs through. One client's
        // red quality rating is everyone's problem, so it needs a screen of its own.
        title: 'WhatsApp',
        path: '/boss/whatsapp',
        icon: getIconfy('mdi:whatsapp'),
        allowed: { boss: ['super_admin'] },
      },
      {
        // The second factor on a Printly device console. Issued here so the
        // capability to open a root shell inside a customer's office is ours to
        // hand over and ours to withdraw.
        title: 'Printly Access',
        path: '/boss/printly',
        icon: getIconfy('mdi:printer-eye'),
        allowed: { boss: ['super_admin'] },
      },
      {
        // What code every Pi in the field runs. Publishing here reaches more
        // hardware in one click than anything else in the product does.
        title: 'Printly Releases',
        path: '/boss/printly/releases',
        icon: getIconfy('mdi:package-variant-closed'),
        allowed: { boss: ['super_admin'] },
      },
      {
        // What every org has left, what each model costs us, and the margin
        // between the two. The only screen in the product where our vendor
        // cost appears.
        title: 'AI Credits',
        path: '/boss/ai-credits',
        icon: getIconfy('mdi:robot-outline'),
        allowed: { boss: ['super_admin'] },
      },
      {
        // The catalog orgs buy credits, add-ons and features from, plus every
        // purchase and the two buttons that rescue a delivery that failed
        // after we took the money.
        title: 'Store',
        path: '/boss/store',
        icon: getIconfy('mdi:storefront-outline'),
        allowed: { boss: ['super_admin'] },
      },
      {
        // Which plan modules each org actually uses, org by module. Dex nudges
        // build on this; managers and RMs see their own orgs.
        title: 'Adoption',
        path: '/boss/adoption',
        icon: getIconfy('mdi:chart-donut'),
        allowed: { boss: BOSS_STAFF_ROLES },
      },
      {
        // Training calls orgs book with the DeskOS team: the request queue,
        // what we offer and at what price, and the working hours slots come from.
        title: 'Training',
        path: '/boss/training/requests',
        icon: getIconfy('mdi:school-outline'),
        allowed: { boss: TRAINING_ROLES },
        children: [
          {
            title: 'Requests',
            path: '/boss/training/requests',
            allowed: { boss: TRAINING_ROLES },
          },
          {
            title: 'Sessions & Pricing',
            path: '/boss/training/sessions',
            allowed: { boss: TRAINING_ROLES },
          },
          {
            title: 'Settings',
            path: '/boss/training/settings',
            allowed: { boss: TRAINING_ROLES },
          },
        ],
      },
    ],
  },
  {
    subheader: 'Support Desk',
    items: [
      {
        title: 'Support Tickets',
        path: '/boss/support/tickets',
        icon: getIconfy('mdi:face-agent'),
        allowed: { boss: ['super_admin', 'relationship_manager'] },
      },
      {
        title: 'Support Config',
        path: '/boss/support/config',
        icon: getIconfy('mdi:cog-outline'),
        allowed: { boss: ['super_admin'] },
      },
    ],
  },
  {
    subheader: 'Sales CRM',
    items: [
      {
        title: 'My Day',
        path: '/boss/crm/my-day',
        icon: getIconfy('mdi:target-account'),
        allowed: { boss: ['super_admin', 'manager'] },
      },
      {
        title: 'Sales Dashboard',
        path: '/boss/crm/dashboard',
        icon: getIconfy('mdi:view-dashboard-outline'),
        allowed: { boss: ['super_admin'] },
      },
      {
        title: 'Pipeline',
        path: '/boss/crm/accounts',
        icon: getIconfy('mdi:sale'),
        allowed: { boss: ['super_admin', 'manager'] },
      },
      {
        title: 'Activity Log',
        path: '/boss/crm/activities',
        icon: getIconfy('mdi:calendar-check-outline'),
        allowed: { boss: ['super_admin', 'manager'] },
      },
      {
        title: 'Contacts',
        path: '/boss/crm/contacts',
        icon: getIconfy('mdi:account-multiple-outline'),
        allowed: { boss: ['super_admin', 'manager'] },
      },
      {
        title: 'Team Performance',
        path: '/boss/crm/team',
        icon: getIconfy('mdi:chart-line'),
        allowed: { boss: ['super_admin'] },
      },
      {
        // Tokens that let an AI assistant work this pipeline. Yours only, so a
        // manager sees the page too.
        title: 'Agent Connections',
        path: '/boss/crm/connections',
        icon: getIconfy('mdi:robot-happy-outline'),
        allowed: { boss: ['super_admin', 'manager'] },
      },
    ],
  },
  {
    subheader: 'Build',
    items: [
      {
        // Everything sitting still because of you, whichever role you are.
        // The count comes from the same endpoint the nudges do.
        title: 'My Work',
        path: '/boss/build/my-work',
        icon: getIconfy('mdi:briefcase-check-outline'),
        allowed: { boss: BUILD_ROLES },
        badgeSource: 'deployQueue',
      },
      {
        title: 'Board',
        path: '/boss/build/board',
        icon: getIconfy('mdi:view-column-outline'),
        allowed: { boss: BUILD_ROLES },
      },
      {
        title: 'Backlog',
        path: '/boss/build/backlog',
        icon: getIconfy('mdi:format-list-bulleted'),
        allowed: { boss: BUILD_ROLES },
      },
      {
        title: 'Sprints',
        path: '/boss/build/sprints',
        icon: getIconfy('mdi:run-fast'),
        allowed: { boss: BUILD_ROLES },
      },
      {
        title: 'Releases',
        path: '/boss/build/deploys',
        icon: getIconfy('mdi:source-branch'),
        allowed: { boss: BUILD_ROLES },
      },
      {
        title: 'Merge Queue',
        path: '/boss/build/queue',
        icon: getIconfy('mdi:source-merge'),
        allowed: { boss: BUILD_ROLES },
      },
      {
        title: 'Repositories',
        path: '/boss/build/repos',
        icon: getIconfy('mdi:git'),
        allowed: { boss: BUILD_ROLES },
      },
      {
        // Tokens that let an AI assistant read stories and move cards. Yours
        // only, so a developer sees the page too.
        title: 'Agent Connections',
        path: '/boss/build/connections',
        icon: getIconfy('mdi:robot-happy-outline'),
        allowed: { boss: BUILD_ROLES },
      },
    ],
  },
  {
    subheader: 'Website',
    items: [
      {
        title: 'Blog Posts',
        path: '/boss/website/posts',
        icon: getIconfy('mdi:post-outline'),
        allowed: { boss: ['super_admin', 'publisher'] },
      },
      {
        title: 'Categories & Authors',
        path: '/boss/website/taxonomy',
        icon: getIconfy('mdi:tag-multiple-outline'),
        allowed: { boss: ['super_admin', 'publisher'] },
      },
      {
        title: 'Media Library',
        path: '/boss/website/media',
        icon: getIconfy('mdi:image-multiple-outline'),
        allowed: { boss: ['super_admin', 'publisher'] },
      },
    ],
  },
];

const navConfig = [
  {
    roles: ['org'],
    items: adminNavConfig,
  },
  {
    roles: ['user'],
    items: userNavConfig,
  },
  {
    roles: ['restaurant-admin'],
    items: restaurantAdminNavConfig,
  },
  {
    roles: ['chef'],
    items: chefNavConfig,
  },
  {
    roles: ['boss'],
    items: bossNavConfig,
  },
];

export default navConfig;